import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

// iter413bj — Theme provider with 3 modes: "light" | "dark" | "system".
// "system" follows prefers-color-scheme media query in real-time.
//
// Persistence: localStorage key "theme" (read by AppearanceTab too).
// Effect: toggles `.dark` className on <html> so Tailwind `dark:` variants apply
// and global CSS rules in index.css (under @media (prefers-color-scheme: dark)
// AND .dark { ... }) take effect.

const ThemeContext = createContext();

const VALID_MODES = ["light", "dark", "system"];

function getSystemDark() {
  return typeof window !== "undefined"
    && window.matchMedia
    && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function readSavedMode() {
  if (typeof window === "undefined") return "light";
  const saved = localStorage.getItem("theme");
  return VALID_MODES.includes(saved) ? saved : "light";
}

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(readSavedMode);
  const [systemDark, setSystemDark] = useState(getSystemDark);

  // Listen for OS theme changes (only matters in "system" mode)
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e) => setSystemDark(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  const isDark = mode === "dark" || (mode === "system" && systemDark);

  // Apply / remove .dark class + persist
  useEffect(() => {
    const root = document.documentElement;
    if (isDark) root.classList.add("dark");
    else root.classList.remove("dark");
    try { localStorage.setItem("theme", mode); } catch { /* ignore */ }
  }, [isDark, mode]);

  const setMode = useCallback((m) => {
    if (VALID_MODES.includes(m)) setModeState(m);
  }, []);

  const toggleTheme = useCallback(() => {
    setModeState(prev => prev === "dark" ? "light" : "dark");
  }, []);

  return (
    <ThemeContext.Provider value={{ isDark, mode, setMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
